import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../UIToolkit/molecules/EmptyState";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";

type TeamEmptyStateProps = {
	hasFiltersApplied: boolean;
	totalStaffCount: number;
	/** The roster couldn't be loaded, so "no staff" would be a guess. */
	loadFailed: boolean;
	onClearFilters: () => void;
	onAddStaff: () => void;
};

/**
 * The roster's no-rows state, in three flavours: the roster failed to load (say so, offer nothing to
 * click), a filter that matched nobody (offer to clear it) and a genuinely empty department (offer to
 * add someone). Distinguishing them matters — "no staff found" next to an Add Staff button is actively misleading when the department has 300 people and
 * a filter is hiding all of them.
 */
export const TeamEmptyState: FC<TeamEmptyStateProps> = ({ hasFiltersApplied, totalStaffCount, loadFailed, onClearFilters, onAddStaff }) => {
	const { t } = useTranslation();
	const showFilteredState = hasFiltersApplied && totalStaffCount > 0;

	if (loadFailed) {
		return (
			<EmptyState
				dataTestId="team-empty-state-load-failed"
				icon={<ErrorOutlineOutlinedIcon />}
				title={t("team.empty.load_failed.title")}
				hint={t("team.empty.load_failed.body")}
			/>
		);
	}

	return (
		<EmptyState
			dataTestId="team-empty-state"
			icon={showFilteredState ? <FilterAltOutlinedIcon /> : <PeopleAltOutlinedIcon />}
			title={showFilteredState ? t("team.empty.filtered.title") : t("team.empty.no_staff.title")}
			hint={showFilteredState ? t("team.empty.filtered.body") : t("team.empty.no_staff.body")}
			action={
				showFilteredState ? (
					<SystemButton data-testid="team-empty-clear-filters" buttonVariant="secondary" onClick={onClearFilters} noFullWidthMobile>
						{t("team.empty.filtered.action")}
					</SystemButton>
				) : (
					<SystemButton
						data-testid="team-empty-add-staff"
						buttonVariant="primary"
						onClick={onAddStaff}
						leadingIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: "18px" }} />}
						noFullWidthMobile
					>
						{t("team.header.add_staff")}
					</SystemButton>
				)
			}
		/>
	);
};
